import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';

class NotificationService {
    constructor() {
        // Don't access Notification.permission directly - it might not exist
        this.permission = null;
        this.subscription = null;
        
        // Check if Notification API is available and set permission
        if ('Notification' in window) {
            this.permission = Notification.permission;
        }
    }

    /**
     * Check if push notifications are supported
     */
    isSupported() {
        return 'Notification' in window && 
               'serviceWorker' in navigator && 
               'PushManager' in window;
    }

    /**
     * Request notification permission from the user
     */
    async requestPermission() {
        if (!this.isSupported()) {
            console.log('Push notifications are not supported in this browser');
            return false;
        }

        try {
            const permission = await Notification.requestPermission();
            this.permission = permission;
            
            if (permission === 'granted') {
                console.log('Notification permission granted');
                return true;
            } else {
                console.log('Notification permission denied');
                return false;
            }
        } catch (error) {
            console.error('Error requesting notification permission:', error);
            return false;
        }
    }

    /**
     * Get the current permission status
     */
    getPermissionStatus() {
        if (!('Notification' in window)) {
            return 'unsupported';
        }
        return this.permission || Notification.permission;
    }

    /**
     * Subscribe to push notifications
     */
    async subscribeToPush() {
        if (!this.isSupported() || this.permission !== 'granted') {
            return null;
        }

        try {
            const registration = await navigator.serviceWorker.ready;
            
            // Check if already subscribed
            let subscription = await registration.pushManager.getSubscription();
            
            if (!subscription) {
                // VAPID public key - you'll need to generate this for your Firebase project
                const vapidPublicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY || 
                    'BKagOny0KF_2pCJQ3m....'; // Replace with your actual VAPID key
                
                const convertedVapidKey = this.urlBase64ToUint8Array(vapidPublicKey);
                
                subscription = await registration.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: convertedVapidKey
                });
            }
            
            this.subscription = subscription;
            return subscription;
        } catch (error) {
            console.error('Error subscribing to push notifications:', error);
            return null;
        }
    }

    /**
     * Save subscription to Firestore
     */
    async saveSubscription(userId, subscription) {
        if (!userId || !subscription) return;

        try {
            const userRef = doc(db, 'users', userId);
            await updateDoc(userRef, {
                pushSubscription: JSON.stringify(subscription),
                notificationEnabled: true,
                lastSubscriptionUpdate: new Date().toISOString()
            });
            console.log('Push subscription saved to Firestore');
        } catch (error) {
            console.error('Error saving push subscription:', error);
        }
    }

    /**
     * Show a local notification (when app is in foreground)
     */
    async showNotification(title, body, data = {}) {
        if (!this.isSupported() || this.permission !== 'granted') {
            return;
        }

        try {
            const registration = await navigator.serviceWorker.ready;
            
            const notificationOptions = {
                body,
                icon: '/logo192.png',
                badge: '/logo192.png',
                tag: data.tag || 'valet-message',
                data: {
                    url: data.url || '/active-chat',
                    ...data
                },
                requireInteraction: true,
                vibrate: [200, 100, 200],
                actions: [
                    {
                        action: 'open',
                        title: 'Open Chat'
                    },
                    {
                        action: 'close',
                        title: 'Dismiss'
                    }
                ]
            };

            await registration.showNotification(title, notificationOptions);
        } catch (error) {
            console.error('Error showing notification:', error);
        }
    }

    /**
     * Play notification sound
     */
    playNotificationSound() {
        try {
            const audio = new Audio('/notification-sound.mp3'); // Add this file to public folder
            audio.volume = 0.5;
            audio.play().catch(e => console.log('Could not play notification sound:', e));
        } catch (error) {
            console.error('Error playing notification sound:', error);
        }
    }

    /**
     * Helper function to convert VAPID key
     */
    urlBase64ToUint8Array(base64String) {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding)
            .replace(/\-/g, '+')
            .replace(/_/g, '/');

        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);

        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    }

    /**
     * Unsubscribe from push notifications
     */
    async unsubscribe() {
        if (this.subscription) {
            try {
                await this.subscription.unsubscribe();
                this.subscription = null;
                console.log('Unsubscribed from push notifications');
                return true;
            } catch (error) {
                console.error('Error unsubscribing from push notifications:', error);
                return false;
            }
        }
        return false;
    }

    /**
     * Check if the app is currently visible
     */
    isAppVisible() {
        return document.visibilityState === 'visible';
    }
}

// Export singleton instance
export default new NotificationService();